import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons/faEllipsis';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { faArrowUp } from '@fortawesome/pro-light-svg-icons/faArrowUp';
import { faArrowDown } from '@fortawesome/pro-light-svg-icons/faArrowDown';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { TypedMemo } from '../../../common/types';
import { blockMessages } from './blockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockRow.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type BlockMenuAction = 'edit' | 'up' | 'down' | 'duplicate' | 'toggleHidden' | 'delete';

interface SMLBlockMenuProps {
  name: string;
  isHidden: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDuplicate: boolean;
  onAction: (action: BlockMenuAction) => void;
}

/** The menu of a row (design a2): Edit, Move up, Move down, Duplicate, Hide or Show, Delete. */
const SMLBlockMenuComponent: React.FC<SMLBlockMenuProps> = ({
  name,
  isHidden,
  canMoveUp,
  canMoveDown,
  canDuplicate,
  onAction,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const onEdit = useCallback(() => onAction('edit'), [onAction]);
  const onUp = useCallback(() => onAction('up'), [onAction]);
  const onDown = useCallback(() => onAction('down'), [onAction]);
  const onDuplicate = useCallback(() => onAction('duplicate'), [onAction]);
  const onToggleHidden = useCallback(() => onAction('toggleHidden'), [onAction]);
  const onDelete = useCallback(() => onAction('delete'), [onAction]);

  return (
    <Dropdown isOpen={isOpen} toggle={toggle} direction="down">
      <DropdownToggle
        color="link"
        className={styles.iconButton}
        aria-label={intl.formatMessage(blockMessages.menu, { name })}
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={onEdit}>
          <FontAwesomeIcon icon={faPen} className={styles.menuIcon} />
          {intl.formatMessage(blockMessages.menuEdit)}
        </DropdownItem>
        <DropdownItem onClick={onUp} disabled={!canMoveUp}>
          <FontAwesomeIcon icon={faArrowUp} className={styles.menuIcon} />
          {intl.formatMessage(blockMessages.menuUp)}
        </DropdownItem>
        <DropdownItem onClick={onDown} disabled={!canMoveDown}>
          <FontAwesomeIcon icon={faArrowDown} className={styles.menuIcon} />
          {intl.formatMessage(blockMessages.menuDown)}
        </DropdownItem>
        {canDuplicate && (
          <DropdownItem onClick={onDuplicate}>
            <FontAwesomeIcon icon={faCopy} className={styles.menuIcon} />
            {intl.formatMessage(blockMessages.menuDuplicate)}
          </DropdownItem>
        )}
        <DropdownItem onClick={onToggleHidden}>
          <FontAwesomeIcon icon={isHidden ? faEye : faEyeSlash} className={styles.menuIcon} />
          {intl.formatMessage(isHidden ? blockMessages.show : blockMessages.hide)}
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem className="text-danger" onClick={onDelete}>
          <FontAwesomeIcon icon={faTrash} className={styles.menuIcon} />
          {intl.formatMessage(blockMessages.menuDelete)}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const SMLBlockMenu = TypedMemo(SMLBlockMenuComponent);
